import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const PERF_TRACKING_ENABLED = import.meta.env.VITE_ENABLE_PERF_TRACKING !== 'false';
const API_URL = (import.meta.env.VITE_API_URL as string).replace(/\/$/, '');
const PAGE_PERF_ENDPOINT = `${API_URL}/api/audit/page-performance`;

/**
 * Get or create a session ID for grouping page performance metrics.
 * Persists in sessionStorage (clears when tab closes).
 */
function getOrCreateSessionId(): string {
  const key = 'perf_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

/**
 * Get connection type with vendor prefix fallbacks.
 */
function getConnectionType(): string | null {
  const nav = navigator as any;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  return connection?.effectiveType ?? null;
}

/**
 * Capture resource size metrics using Resource Timing API.
 * Returns total transfer size, resource size, and identifies the largest resource.
 */
function captureResourceSizeMetrics(): {
  totalTransferSize: number;
  totalResourceSize: number;
  largestResourceSize: number;
  largestResourceUrl: string | null;
} {
  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  
  let totalTransferSize = 0;
  let totalResourceSize = 0;
  let largestResourceSize = 0;
  let largestResourceUrl: string | null = null;
  
  resources.forEach((resource) => {
    // transferSize = compressed bytes over network
    // decodedBodySize = uncompressed size
    totalTransferSize += resource.transferSize || 0;
    totalResourceSize += resource.decodedBodySize || 0;
    
    if (resource.transferSize > largestResourceSize) {
      largestResourceSize = resource.transferSize;
      largestResourceUrl = resource.name;
    }
  });
  
  return {
    totalTransferSize: Math.round(totalTransferSize),
    totalResourceSize: Math.round(totalResourceSize),
    largestResourceSize: Math.round(largestResourceSize),
    largestResourceUrl,
  };
}

/**
 * Capture Largest Contentful Paint using PerformanceObserver.
 * Returns the LCP value in milliseconds.
 */
function captureLCP(): Promise<number | null> {
  return new Promise((resolve) => {
    if (!('PerformanceObserver' in window)) {
      resolve(null);
      return;
    }

    let lcpValue: number | null = null;
    
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        // The last entry is the final LCP
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          lcpValue = Math.round(lastEntry.startTime);
        }
      });

      observer.observe({ type: 'largest-contentful-paint', buffered: true });

      // Return value after short delay to capture final LCP
      setTimeout(() => {
        observer.disconnect();
        resolve(lcpValue);
      }, 500);
    } catch {
      // Browser doesn't support LCP observer
      resolve(null);
    }
  });
}

/**
 * Capture Total Blocking Time using PerformanceObserver for long tasks.
 * TBT = sum of (task duration - 50ms) for all tasks > 50ms.
 */
function captureTBT(): Promise<number> {
  return new Promise((resolve) => {
    if (!('PerformanceObserver' in window)) {
      resolve(0);
      return;
    }

    let totalBlockingTime = 0;

    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            totalBlockingTime += entry.duration - 50;
          }
        }
      });

      observer.observe({ type: 'longtask', buffered: true });

      // Return value after short delay to capture accumulated TBT
      setTimeout(() => {
        observer.disconnect();
        resolve(Math.round(totalBlockingTime));
      }, 500);
    } catch {
      // Browser doesn't support longtask observer
      resolve(0);
    }
  });
}

/**
 * Capture full page metrics using Navigation Timing API.
 * Only accurate for initial page load, not SPA navigations.
 */
async function captureFullPageMetrics(path: string): Promise<Record<string, unknown> | null> {
  const entries = performance.getEntriesByType('navigation');
  if (entries.length === 0) return null;

  const perfData = entries[0] as PerformanceNavigationTiming;
  
  // Ensure load event has completed
  if (perfData.loadEventEnd === 0) return null;

  const paintEntries = performance.getEntriesByType('paint');
  const fp = paintEntries.find(e => e.name === 'first-paint');
  const fcp = paintEntries.find(e => e.name === 'first-contentful-paint');
  
  const resourceMetrics = captureResourceSizeMetrics();
  
  // Capture LCP and TBT asynchronously
  const [lcp, tbt] = await Promise.all([captureLCP(), captureTBT()]);

  return {
    url: window.location.href,
    path,
    
    // Network timing
    dns_time: Math.round(perfData.domainLookupEnd - perfData.domainLookupStart),
    tcp_time: Math.round(perfData.connectEnd - perfData.connectStart),
    ttfb: Math.round(perfData.responseStart - perfData.requestStart),
    download_time: Math.round(perfData.responseEnd - perfData.responseStart),
    
    // DOM timing (using fetchStart as baseline per reference code)
    dom_processing_time: Math.round(perfData.domComplete - perfData.domInteractive),
    dom_content_loaded: Math.round(perfData.domContentLoadedEventEnd - perfData.fetchStart),
    full_page_load: Math.round(perfData.loadEventEnd - perfData.fetchStart),
    
    // Paint timing
    first_paint: fp ? Math.round(fp.startTime) : null,
    first_contentful_paint: fcp ? Math.round(fcp.startTime) : null,
    largest_contentful_paint: lcp,
    total_blocking_time: tbt,
    
    // Context
    resource_count: performance.getEntriesByType('resource').length,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    connection_type: getConnectionType(),
    
    // Page weight metrics
    total_transfer_size: resourceMetrics.totalTransferSize,
    total_resource_size: resourceMetrics.totalResourceSize,
    largest_resource_size: resourceMetrics.largestResourceSize,
    largest_resource_url: resourceMetrics.largestResourceUrl,
    
    session_id: getOrCreateSessionId(),
    is_spa_navigation: false,
  };
}

/**
 * Capture SPA navigation metrics using performance.now() timing.
 * Measures actual React render time for client-side route changes.
 */
function captureSpaMetrics(path: string, spaLoadTime: number): Record<string, unknown> {
  const resourceMetrics = captureResourceSizeMetrics();
  
  return {
    url: window.location.href,
    path,
    
    // SPA navigations don't have network timing (assets already cached)
    dns_time: 0,
    tcp_time: 0,
    ttfb: 0,
    download_time: 0,
    
    // DOM timing reflects actual React render time
    dom_processing_time: spaLoadTime,
    dom_content_loaded: spaLoadTime,
    full_page_load: spaLoadTime,
    
    // Paint timing not applicable to SPA navigations
    first_paint: null,
    first_contentful_paint: null,
    largest_contentful_paint: null,
    total_blocking_time: 0,
    
    // Context
    resource_count: performance.getEntriesByType('resource').length,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    connection_type: getConnectionType(),
    
    // Page weight metrics (includes any new resources loaded for this route)
    total_transfer_size: resourceMetrics.totalTransferSize,
    total_resource_size: resourceMetrics.totalResourceSize,
    largest_resource_size: resourceMetrics.largestResourceSize,
    largest_resource_url: resourceMetrics.largestResourceUrl,
    
    session_id: getOrCreateSessionId(),
    is_spa_navigation: true,
  };
}

/**
 * Send metrics to the Railway audit route using fire-and-forget pattern.
 * Uses fetch with keepalive so the request survives page navigation.
 *
 * Server derives company_id from the authenticated profile, so we don't
 * include it in the payload.
 */
async function sendMetrics(
  metrics: Record<string, unknown>,
  accessToken: string
): Promise<void> {
  try {
    fetch(PAGE_PERF_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(metrics),
      keepalive: true, // Survives page navigation
    });
    // No await - fire and forget
  } catch (error) {
    // Silently fail - telemetry should not impact user experience
    console.debug('Failed to send page performance metrics:', error);
  }
}

/**
 * Hook to capture and send page performance metrics.
 * Integrates into AppLayout to automatically track all protected routes.
 * 
 * Distinguishes between:
 * - Initial page load: Uses Navigation Timing API for accurate cold start metrics
 * - SPA navigations: Uses performance.now() to measure actual React render time
 * 
 * Metrics are sent after rendering completes using requestIdleCallback
 * to avoid impacting page performance.
 */
export function usePagePerformance(): void {
  const location = useLocation();
  const { user } = useAuth();
  const lastTrackedPath = useRef<string | null>(null);
  const isInitialLoad = useRef(true);
  const navStartTime = useRef<number>(0);

  useEffect(() => {
    // Skip if tracking is disabled via environment variable
    if (!PERF_TRACKING_ENABLED) return;

    // Only track if user is authenticated
    if (!user) return;

    const currentPath = location.pathname;

    // Avoid duplicate tracking for the same path
    if (lastTrackedPath.current === currentPath) return;

    // Mark SPA navigation start time immediately
    navStartTime.current = performance.now();

    const trackPerformance = async () => {
      try {
        // Server pulls the user/company from the JWT, so we just need the token
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        if (!accessToken) return;

        // For initial page load, wait for load event and use Navigation Timing API
        if (isInitialLoad.current) {
          const captureAndSend = async () => {
            const metrics = await captureFullPageMetrics(currentPath);
            if (metrics) {
              sendMetrics(metrics, accessToken);
              lastTrackedPath.current = currentPath;
            }
          };

          if (document.readyState === 'complete') {
            // Page already loaded, use idle callback
            if ('requestIdleCallback' in window) {
              requestIdleCallback(captureAndSend, { timeout: 2000 });
            } else {
              setTimeout(captureAndSend, 100);
            }
          } else {
            // Wait for load event
            window.addEventListener('load', () => {
              if ('requestIdleCallback' in window) {
                requestIdleCallback(captureAndSend, { timeout: 2000 });
              } else {
                setTimeout(captureAndSend, 100);
              }
            }, { once: true });
          }

          isInitialLoad.current = false;
        } else {
          // SPA navigation: measure time since route change using requestIdleCallback
          const captureAndSendSpa = () => {
            const spaLoadTime = Math.round(performance.now() - navStartTime.current);
            const metrics = captureSpaMetrics(currentPath, spaLoadTime);
            sendMetrics(metrics, accessToken);
            lastTrackedPath.current = currentPath;
          };

          if ('requestIdleCallback' in window) {
            requestIdleCallback(captureAndSendSpa, { timeout: 2000 });
          } else {
            setTimeout(captureAndSendSpa, 100);
          }
        }
      } catch (error) {
        // Silently fail - telemetry should not impact user experience
        console.debug('Page performance tracking error:', error);
      }
    };

    trackPerformance();
  }, [location.pathname, user]);
}
